import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import _groupBy from 'lodash/groupBy';
import { IntlShape } from 'react-intl';
import {
  AdditionalInfo,
  AdditionalInfoAccount,
  ProviderField,
  ProviderFieldAdditionalInfo,
  SupplementalInformationLayoutType,
} from '../../models/provider';
import { ValidationValues } from '../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputValidator';

type OrderedProviderField = ProviderField & {
  index: number;
};

export enum TemplateElementType {
  TEMPLATE = 'TEMPLATE',
  COLOR = 'COLOR',
  INPUT = 'INPUT',
  ICON = 'ICON',
  TEXT = 'TEXT',
  OPEN_THIRD_PARTY = 'OPEN_THIRD_PARTY',
  CHANGE_METHOD = 'CHANGE_METHOD',
  NOTICE = 'NOTICE',
  IMAGE = 'IMAGE',
}

export enum TemplateElementStyle {
  TITLE = 'TITLE',
  TEXT = 'TEXT',
  IDENTITY_HINT = 'IDENTITY_HINT',
  POSITIONAL_INPUT = 'POSITIONAL_INPUT',
  ORDERED_LIST = 'ORDERED_LIST',
  INSTRUCTION = 'INSTRUCTION',
  WARNING = 'WARNING',
  IMAGE = 'IMAGE',
}

export type TemplateOrder = {
  key: 'type' | 'style';
  value: TemplateElementType | TemplateElementStyle;
  slot?: string;
};

export function findElementOrder(templateOrder: TemplateOrder[], element: ProviderField) {
  return templateOrder.find((order) => {
    const isMatch = element[order.key] === order.value;

    return isMatch;
  });
}

export function groupBySlot(templateOrder: TemplateOrder[], elements: ProviderField[]) {
  return _groupBy(elements, (element) => {
    const match = findElementOrder(templateOrder, element);

    if (!match) {
      return 'unknown';
    }

    // If no slot is defined, assume the element is part of
    // the main area.
    return match.slot || 'main';
  });
}

function assignElementOrder(
  templateOrder: TemplateOrder[],
  element: ProviderField
): OrderedProviderField {
  const elementOrder = findElementOrder(templateOrder, element);

  if (!elementOrder) {
    return {
      ...element,
      // fallback to an order that will place the element at the bottom
      index: 100,
    };
  }

  return {
    ...element,
    index: templateOrder.indexOf(elementOrder),
  };
}

export function sortTemplateElements(templateOrder: TemplateOrder[], elements: ProviderField[]) {
  return elements
    .map((el) => assignElementOrder(templateOrder, el))
    .sort((a, b) => {
      return a.index - b.index;
    });
}

export const getErrorMessage = (
  { errorMessage, errorMessageValues }: ValidationValues,
  intl: IntlShape
) => {
  if (!errorMessage) {
    return '';
  }

  if (typeof errorMessage === 'string') {
    return errorMessage;
  }

  return intl.formatMessage(errorMessage, errorMessageValues);
};

export enum Templates {
  // New templates
  APP_CODE = 'APP_CODE',
  DECOUPLED = 'DECOUPLED',
  SMS_CODE = 'SMS_CODE',
  CARD_READER = 'CARD_READER',
  CHIP_TAN = 'CHIP_TAN',
  OTP_VERIFICATION_METHODS = 'OTP_VERIFICATION_METHODS',
  VERIFICATION = 'VERIFICATION',
  IMAGE_SELECTION = 'IMAGE_SELECTION',
  IMAGE_CODE = 'IMAGE_CODE',
  // Old templates
  SMS_TAN_IS_NOT_IN_PROD_YET = 'SMS_TAN_IS_NOT_IN_PROD_YET',
  QR_CODE = 'QR_CODE',
}

export const checkIsTemplateScreen = (fields: ProviderField[]): boolean => {
  const template = filter(fields, { type: 'TEMPLATE' });

  return !isEmpty(template);
};

/**
 * To correctly handle supplementalInformation screens when querying credentials
 * using `?templatesScreen=true` we need to exclude any template specific elements
 * in the case we don't yet have matching template. This will make sure the screens
 * that existed before the introduction of template screens will still work as before.
 *
 * @example fields.filter((field) => isLegacyField(field));
 */
export const isLegacyField = (field: ProviderField) => {
  // fields may only have `type` and/or `style`
  // when querying credentials with `?templatesScreen=true`
  if (!field.type && !field.style && Object.keys(field).length) {
    return true;
  }

  // To maintain backwards compatibility when querying credentials with `?templatesScreen=true`
  // legacy fields might get `type` and/or `style`. Only the combinations below might represent
  // a legacy field.
  const validLegacyTypes = ['INPUT', 'TEXT'];
  const validLegacyStyles = ['INPUT', 'TEXT', 'INSTRUCTION'];
  const isValidLegacyType = field.type && validLegacyTypes.includes(field.type);
  const isValidLegacyStyle =
    field.style === null || (field.style && validLegacyStyles.includes(field.style));

  return (isValidLegacyType && isValidLegacyStyle) || field.selectOptions;
};

export const getLegacyFields = (fields: ProviderField[]) => fields.filter(isLegacyField);

type TemplateField = ProviderField & {
  type: 'TEMPLATE';
  value: Templates;
};

function isTemplateField(field: ProviderField): field is TemplateField {
  return field.type === 'TEMPLATE';
}

export const getTemplateField = (fields: ProviderField[]) => {
  const templateField = fields.find((field) => field.type === 'TEMPLATE');

  if (templateField && isTemplateField(templateField)) {
    return templateField;
  }

  return null;
};

export const hasTemplateField = (fields: ProviderField[]) => Boolean(getTemplateField(fields));

export const hasTemplateFieldOfType = (fields: ProviderField[] | undefined, type: Templates) => {
  if (!fields) return false;

  const templateField = getTemplateField(fields);

  return Boolean(templateField && templateField.value === type);
};

export const hasSingleSelectLegacyField = (fields: ProviderField[]) =>
  fields.length === 1 && Boolean(fields[0].selectOptions);

/* Not yet in use, will be used for Image Captcha like MFAs */
export const hasMultiSelectImageField = (_fields: ProviderField[]) => false;

export const hasAdditionalInfo = (
  additionalInfo: ProviderField['additionalInfo']
): additionalInfo is AdditionalInfo => {
  return additionalInfo !== null && typeof additionalInfo !== 'string';
};

export const hasAdditionalInfoAccount = (
  additionalInfo: AdditionalInfo
): additionalInfo is AdditionalInfoAccount => {
  return Boolean((additionalInfo as AdditionalInfoAccount).accountNumber);
};

export const hasInstructionsLegacyField = (fields: ProviderField[]) => {
  if (fields.length !== 1) {
    return false;
  }

  const field = fields[0];

  if (hasAdditionalInfo(field.additionalInfo)) {
    return (
      (field.additionalInfo as ProviderFieldAdditionalInfo).layoutType ===
      SupplementalInformationLayoutType.INSTRUCTIONS
    );
  }

  return false;
};

export const hasFields = (fields: ProviderField[]) => Boolean(fields.length);

export const isCheckboxFields = (fields: ProviderField[]) => {
  return fields.every((field) => field.checkbox);
};
